/**
 * The LV surface registry — the client-side mirror of surfaces.json (the
 * release manifest CapCom's Learner Surfaces tab reads). Every LV surface is
 * listed here exactly once: main.tsx uses the routes to decide whether the
 * shell boots, and the shell uses the components to mount tabs. The lazy()
 * wrappers live here so the chunk split and the component identity are
 * module-level stable — a lazy() created inside a render would remount the
 * surface (and lose its state) on every shell re-render.
 *
 * THE HARNESS HOLDS NO SURFACE CODE. Each surface lives in its own (local-git)
 * repo and is synced into src/surfaces/<id>/ by scripts/sync-surfaces.mjs
 * (pins in surfaces.config.json). This registry discovers whatever was synced
 * via the surface.json fragments — adding a surface needs NO harness edit.
 */
import { lazy, type ComponentType } from 'react'

/** What a surface can be opened WITH — the shell-to-surface bridge (the
 *  chat's SurfaceOpenTarget shape, mirrored so the registry stays
 *  chat-free). `params.menu` is the launcher-flyout pick: a surface whose
 *  manifest declares `menuOp` receives the picked item's id there.
 *  `params.face` is the two-sided face; `tabId` is shell-reserved — the
 *  hosting tab's own id, so the surface can ask the shell to flip it.
 *  `label` is the tab's own name (e.g. the pool a flyout pick named the
 *  tab after) — a surface can render it immediately instead of flashing a
 *  generic title while its first read loads. */
export interface SurfaceTarget {
  view?: string
  params?: Record<string, unknown>
  tabId?: string
  label?: string
}

/** The props every surface component must tolerate (they're free to ignore
 *  them — a no-props component is assignable to this). */
export interface SurfaceProps {
  target?: SurfaceTarget
}

export interface LvSurfaceDef {
  id: string
  label: string
  /** URL path the LMS handoff redirects to (e.g. InboxSurface.aspx → /inbox). */
  route: string
  /** @mui/icons-material export name, per the surface's manifest fragment. */
  icon: string
  /** Data-plane op the shell queries for launcher-flyout items
   *  ({items:[{id,label}]}); a pick opens the surface with
   *  target.params.menu = item id. Absent = plain launcher row. */
  menuOp?: string
  /** Metered surfaces (surface.json): the launcher row stays HIDDEN until the
   *  menuOp fetch proves ≥1 item — fail-closed: unfetched/failed = empty. */
  hideWhenMenuEmpty?: boolean
  /** Settings-store external-target override (nav.<id>.href): launchers open
   *  this URL in a new tab instead of a surface tab. */
  href?: string
  /** Settings-store auto-open (nav.<id>.auto), applied by resolveSurfaces:
   *  the shell pre-opens the surface at login. Absent/false = launch only. */
  auto?: boolean
  /** Settings-store locked tabs (nav.<id>.locked), applied by
   *  resolveSurfaces: a tab of this surface can never be closed. */
  locked?: boolean
  /** Settings-store pin affordance (nav.<id>.allowPinning), applied by
   *  resolveSurfaces with INVERTED polarity: absent = pinning allowed;
   *  false = no pin control and stored pins are void. */
  allowPinning?: boolean
  /** Settings-store close enforcement (nav.<id>.allowClose), same inverted
   *  polarity: false = an AUTO-LOADED tab of this surface can't be closed
   *  (learner-opened tabs still close). */
  allowClose?: boolean
  /** Two-sided surfaces: the faces a tab may flip between (surface.json —
   *  e.g. connections' participant/leader). Absent = single-faced, no flip
   *  affordance anywhere. The flip is presentation only; the data worker
   *  re-gates every op server-side. */
  faces?: { id: string; label: string }[]
  /** The face a menu-scoped tab opens on with no remembered choice
   *  (default: faces[0]). */
  defaultFace?: string
  /** MENULESS (surface.json "menu": false): never listed in launcher/
   *  Explore/Nav & Settings, but still resolved and openable via
   *  openSurfaceTab. Absent = listed. */
  menu?: boolean
  Component: ReturnType<typeof lazy<ComponentType<SurfaceProps>>>
}

interface SurfaceFragment {
  id: string
  label: string
  route: string
  icon: string
  /** Client entry file inside the surface dir (default index.tsx). */
  entry?: string
  /** Launcher-flyout data-plane op (see LvSurfaceDef.menuOp). */
  menuOp?: string
  /** Metered surfaces: the launcher row is hidden until the menu op proves
   *  ≥1 item (see LvSurfaceDef.hideWhenMenuEmpty). */
  hideWhenMenuEmpty?: boolean
  /** Two-sided surfaces (see LvSurfaceDef.faces/defaultFace). */
  faces?: { id: string; label: string }[]
  defaultFace?: string
  /** MENULESS surfaces (surface.json "menu": false) — never listed in the
   *  launcher/Explore or Nav & Settings, never toggleable; still resolved
   *  and openable via openSurfaceTab (details pages opened on click from
   *  other surfaces — e.g. activities). Absent = listed (default). */
  menu?: boolean
}

const fragments = import.meta.glob<SurfaceFragment>('../surfaces/*/surface.json', {
  eager: true,
  import: 'default',
})
const entries = import.meta.glob<{ default: ComponentType<SurfaceProps> }>('../surfaces/*/*.tsx')

export const LV_SURFACES: LvSurfaceDef[] = Object.entries(fragments)
  .map(([path, frag]) => {
    const id = path.split('/')[2]
    if (frag.id !== id) throw new Error(`surface id "${frag.id}" does not match its directory "${id}"`)
    const entry = frag.entry ?? 'index.tsx'
    const loader = entries[`../surfaces/${id}/${entry}`]
    if (!loader) throw new Error(`surface "${id}": entry file ${entry} not found under src/surfaces/${id}/`)
    return { id, label: frag.label, route: frag.route, icon: frag.icon, menuOp: frag.menuOp, hideWhenMenuEmpty: frag.hideWhenMenuEmpty, faces: frag.faces, defaultFace: frag.defaultFace, menu: frag.menu, Component: lazy(loader) }
  })
  .sort((a, b) => a.id.localeCompare(b.id))

/** Route lookup for the boot decision in main.tsx (trailing slashes tolerated). */
export function surfaceByRoute(pathname: string): LvSurfaceDef | undefined {
  const clean = pathname.replace(/\/+$/, '')
  const found = LV_SURFACES.find((s) => s.route === clean)
  if (found) return found
  // Custom pages: '/page/<slug>' deep links boot the shell with the page tab
  // on top — same as a synced surface route (the config gate still decides
  // whether the tab actually opens).
  const m = /^\/page\/([a-z0-9]+(?:-[a-z0-9]+)*)$/.exec(clean)
  if (m) return pageSurfaceById(`page-${m[1]}`)
  return undefined
}

export function surfaceById(id: string): LvSurfaceDef | undefined {
  return LV_SURFACES.find((s) => s.id === id) ?? pageSurfaceById(id)
}

// ── Custom pages (the classic Branding "Add Page", ported) ──────────────────
// Page surface ids are DYNAMIC — `page-<slug>`, emitted by /api/config with
// component 'page'. They are not synced surfaces: the registry synthesizes
// their defs around LvPageCanvas so the shell's tabs/pin/drag/layout-
// persistence machinery treats a page exactly like any surface.

export const PAGE_SURFACE_ID_RE = /^page-([a-z0-9]+(?:-[a-z0-9]+)*)$/

/** Module-level lazy — a lazy() created inside a render would remount the
 *  surface (and lose its state) on every shell re-render, the same rule the
 *  synced-surface Components above follow. */
const LvPageCanvas = lazy(() => import('../pages/LvPageCanvas'))

/** The synthesized def for a page surface id (undefined for non-page ids).
 *  Label/icon/route here are the generic fallbacks — the config's emitted
 *  entry carries the resolved ones, applied by resolveSurfaces. */
export function pageSurfaceById(id: string): LvSurfaceDef | undefined {
  const m = PAGE_SURFACE_ID_RE.exec(id)
  if (!m) return undefined
  return { id, label: m[1], route: `/page/${m[1]}`, icon: 'Description', Component: LvPageCanvas }
}
